import { FC } from 'react'
import { PastTargetsFooterTemplate } from '../../../../components/custom_components/PastTargetsFooterTemplate'
import { useAuth } from '../../../../context/auth-context'
import { useAmountAndCurrency } from '../../../../shared/useAmountAndCurrency'
import {
  ObjectiveStatus,
  ObjectiveDto,
  SalaryOfYearFragment,
  TargetBonusOfYearFragment,
  ContractChangeFragment,
  Currency,
} from '../../../../types'
import { filterObjectivesByReferenceYear, filterObjectivesByStatusAndReferenceYear } from '../../okr/util'
import { calculatePastTargetsData } from './util'
import { ContractWithValidity, FlexibleBonusOverride } from './targetBonusCalculation-util'
import { useCurrency } from '../../../../context/currency-context'

interface Props {
  objectives: ObjectiveDto[]
  targetBonusOfYear: TargetBonusOfYearFragment[]
  salaryOfYear: SalaryOfYearFragment[]
  currentSalary: number
  currentBonus: number
  referenceYear: number
  contractStartDate: string
  contractEndDate?: string
  employeeTimezone: string
  isBonusValidationEnabled: boolean
  contractChangesByEmployee?: ContractChangeFragment[]
  contractsInYear?: ContractWithValidity[]
  pastYearFlexibleBonusOverride?: FlexibleBonusOverride
  flexibleBonusUnknownCurrency?: Currency
}

export const PastTargetsFooter: FC<Props> = ({
  objectives,
  targetBonusOfYear,
  salaryOfYear,
  currentSalary,
  currentBonus,
  referenceYear,
  contractStartDate,
  contractEndDate,
  employeeTimezone,
  isBonusValidationEnabled,
  contractChangesByEmployee = [],
  contractsInYear = [],
  pastYearFlexibleBonusOverride,
  flexibleBonusUnknownCurrency,
}) => {
  const { user } = useAuth()
  const { formatCurrency, calculateAndFormatNewTargetAmount } = useAmountAndCurrency(user.locale)
  const { currency: currentContractCurrency, currencyConversionData } = useCurrency()

  const openTargets = filterObjectivesByStatusAndReferenceYear(objectives, [ObjectiveStatus.Approved], referenceYear).filter(
    (objective) => objective.achievedAmount === null || objective.achievedAmount === undefined,
  )

  const allCurrentTargets = filterObjectivesByReferenceYear(objectives, referenceYear)

  const _formatCurrency = (amount: number) => formatCurrency(amount, currentContractCurrency, true)
  const _calculateAndFormatNewTargetAmount = (amount: number, conversionRate?: number) =>
    calculateAndFormatNewTargetAmount(amount, currentContractCurrency, conversionRate)

  const { openTargetBonus, targetBonusAmount, targetBonusToBePaid, openTargetBonusIsNegative, unknownCurrenciesForConversion } =
    calculatePastTargetsData(
      employeeTimezone,
      isBonusValidationEnabled,
      targetBonusOfYear,
      salaryOfYear,
      currentSalary,
      currentBonus,
      contractChangesByEmployee,
      objectives,
      currencyConversionData,
      currentContractCurrency,
      referenceYear,
      contractStartDate,
      contractEndDate,
      contractsInYear,
      pastYearFlexibleBonusOverride,
      flexibleBonusUnknownCurrency,
    )

  return (
    <PastTargetsFooterTemplate
      annualTargetBonus={targetBonusAmount}
      openTargetBonus={openTargetBonus}
      targetBonusToPay={targetBonusToBePaid}
      formatCurrency={_formatCurrency}
      calculateAndFormatNewTargetAmount={_calculateAndFormatNewTargetAmount}
      totalTargets={allCurrentTargets.length}
      openTargets={openTargets.length}
      openTargetBonusIsNegative={openTargetBonusIsNegative}
      unknownCurrenciesForConversion={unknownCurrenciesForConversion}
    />
  )
}
